export type TError400Details = {
  title: string,
  type: string,
  statusCode: number,
  details: {
    cause: string,
    reason: string,
    violations: {
      type: string,
      title: string,
      detail: string,
      violations: Array<{
        propertyPath: string,
        title: string,
        template: string,
        parameters: { [key: string]: string },
        type: string,
      }>,
    },
  },
};

export type TError401Details = {
  title: string,
  type: string,
  statusCode: 401,
  details: [],
};

export type TError403Details = {
  title: string,
  type: string,
  statusCode: 403,
  details: Record<never, never>,
};

export type TError404Details = {
  title: string,
  type: string,
  statusCode: 404,
  details: [],
};

export type TError409Details = {
  title: string,
  type: string,
  statusCode: 409,
  details: { propertyPath: string },
};

export type TError429Details = {
  title: string,
  type: string,
  statusCode: 429,
  details: Record<never, never>,
};

//
const parseValidationErrors = (errorStr: string): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!errorStr) {
    return {};
  }

  const errorLines = errorStr.split('\n');

  errorLines.forEach((line: string) => {
    const [fieldName, ...errorParts] = line.split(': ');
    const errorMessage = errorParts.join(': ');

    if (fieldName && errorMessage) {
      errors[fieldName.trim()] = errorMessage.trim();
    }
  });

  return errors;
};

export const WRITE_LOCKED_ERROR_TYPE = 'write_locked';

export class HTTP400Error extends Error {
  statusCode = 400;
  name: string;
  validationErrors: Record<string, string> | null = null;

  constructor(
    public response: Response,
    public details: TError400Details,
  ) {
    const message = `API Error: ${details.title}:`
      + `\n\t${details.details?.violations?.detail || details.title}`;
    super(message);
    this.name = `${response.statusText} Error`;
    this.validationErrors = parseValidationErrors(details.details?.violations?.detail || '');
  }

  get isWriteLocked(): boolean {
    return this.details?.type === WRITE_LOCKED_ERROR_TYPE;
  }
}

export const http401Types = {
  USER_BLOCKED: 'authentication_error.user_blocked',
  EMAIL_UNVERIFIED: 'authentication_error.user_email_unverified',
};

export class HTTP401Error extends Error {
  statusCode = 401;
  name: string;

  constructor(
    public response: Response,
    public details: TError401Details,
  ) {
    super(`API Error: ${details.title}`);
    this.name = `${response.statusText} Error`;
  }
}

export class HTTP403Error extends Error {
  statusCode = 403;
  name: string;

  constructor(
    public response: Response,
    public details: TError403Details,
  ) {
    super(`API Error: ${details.title}`);
    this.name = `${response.statusText} Error`;
  }
}

export class HTTP404Error extends Error {
  statusCode = 404;
  name: string;

  constructor(
    public response: Response,
    public details: TError404Details,
  ) {
    super(`API Error: ${details.title}`);
    this.name = `${response.statusText} Error`;
  }
}

export class HTTP409Error extends Error {
  statusCode = 409;
  name: string;

  constructor(
    public response: Response,
    public details: TError409Details,
  ) {
    super(`API Error: ${details.title}: ${details.details.propertyPath}`);
    this.name = `${response.statusText} Error`;
  }
}

export class HTTP429Error extends Error {
  statusCode = 429;
  name: string;

  constructor(
    public response: Response,
    public details: TError429Details,
  ) {
    super(`API Error: ${details.title}`);
    this.name = `${response.statusText} Error`;
  }
}

export class ServerSideError extends Error {
  statusCode: number;
  name: string;
  details = null;

  constructor(public response: Response) {
    super(`API Error: ${response.statusText}`);

    this.statusCode = response.status;
    this.name = `${response.statusText} Error`;
  }
}

export type THTTPError =
  | HTTP400Error
  | HTTP401Error
  | HTTP403Error
  | HTTP404Error
  | HTTP409Error
  | HTTP429Error
  | ServerSideError;

//

export type THandlerProps = {
  skipErrorToast?: (error: THTTPError) => boolean,
};
